import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Landmark,
  Calculator,
  TrendingUp,
  Shield,
  FileText,
  FileCheck,
  MessageCircle,
  CheckCircle2,
  Building2,
  ChevronDown,
  HelpCircle,
} from "lucide-react";

const LOGO_ICON = "https://customer-assets.emergentagent.com/job_direito-trabalho-sp/artifacts/8b5v8061_IconOnly_Transparent_NoBuffer.png";
const WHATSAPP_LINK = "https://wa.me/5511917611575?text=Olá! Gostaria de uma consulta sobre Direito Tributário.";

const TributarioPage = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const faqs = [
    {
      pergunta: "O que é planejamento tributário e como pode ajudar minha empresa?",
      resposta: "Planejamento tributário é a análise legal das operações da empresa para identificar a forma mais econômica de pagar tributos, dentro da lei. Pode envolver escolha do regime tributário (Simples, Lucro Presumido ou Real), aproveitamento de benefícios fiscais e reorganização societária."
    },
    {
      pergunta: "Minha empresa pode recuperar tributos pagos a mais?",
      resposta: "Sim. Se sua empresa pagou tributos indevidamente ou a maior nos últimos 5 anos, é possível solicitar a restituição ou compensação desses valores. Casos comuns incluem exclusão do ICMS da base do PIS/COFINS e contribuições previdenciárias indevidas."
    },
    {
      pergunta: "O que acontece se eu não pagar os tributos em dia?",
      resposta: "O não pagamento gera multa, juros e correção monetária. Além disso, pode resultar em inscrição em dívida ativa, protesto, restrições cadastrais (CADIN) e, em casos graves, execução fiscal com penhora de bens e bloqueio de contas."
    },
    {
      pergunta: "Qual a diferença entre elisão e evasão fiscal?",
      resposta: "Elisão fiscal é o planejamento tributário lícito, que usa meios legais para reduzir a carga tributária. Evasão fiscal é a sonegação, que envolve fraude, omissão ou falsificação para não pagar tributos — sendo crime punível com multas e prisão."
    },
    {
      pergunta: "Como funciona uma execução fiscal?",
      resposta: "É o processo judicial pelo qual a Fazenda Pública cobra dívidas tributárias. Inicia-se com a citação do devedor para pagar em 5 dias. Não havendo pagamento, podem ser penhorados bens, bloqueadas contas bancárias e, em casos de empresas, os sócios podem ser responsabilizados."
    },
    {
      pergunta: "Posso parcelar dívidas tributárias?",
      resposta: "Sim. Existem programas de parcelamento ordinário e especial (como REFIS) que permitem dividir a dívida em até 60 ou mais parcelas, muitas vezes com redução de multas e juros. A análise de cada caso determina a melhor estratégia."
    },
  ];

  const servicos = [
    { icon: <Calculator className="w-5 h-5" />, title: "Planejamento Tributário", desc: "Estruturação legal para redução da carga tributária" },
    { icon: <TrendingUp className="w-5 h-5" />, title: "Recuperação de Créditos", desc: "Identificação e recuperação de tributos pagos indevidamente" },
    { icon: <Shield className="w-5 h-5" />, title: "Defesa em Execuções", desc: "Representação em cobranças judiciais de tributos" },
    { icon: <FileText className="w-5 h-5" />, title: "Autos de Infração", desc: "Defesa administrativa e judicial contra autuações" },
    { icon: <FileCheck className="w-5 h-5" />, title: "Consultoria Fiscal", desc: "Orientação sobre obrigações tributárias" },
    { icon: <Landmark className="w-5 h-5" />, title: "Análise de Riscos", desc: "Diagnóstico para identificar contingências fiscais" },
  ];

  return (
    <>
      <Helmet>
        <title>Advogado Tributário | Planejamento Tributário</title>
        <meta name="description" content="Consultoria e planejamento tributário. Recuperação de créditos fiscais, análise de riscos e defesa em execuções fiscais." />
        <meta name="keywords" content="advogado tributário, planejamento tributário, recuperação créditos fiscais, direito tributário" />
      </Helmet>

      <div>
        {/* Hero */}
        <section className="pt-32 pb-20 bg-gradient-to-br from-emerald-50 to-white relative overflow-hidden">
          <div className="absolute right-0 top-1/2 -translate-y-1/2 opacity-[0.03] pointer-events-none">
            <img src={LOGO_ICON} alt="" className="w-[500px] h-auto" style={{ filter: "brightness(0)" }} />
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <p className="text-emerald-700 font-medium tracking-wider text-sm mb-4">DIREITO TRIBUTÁRIO</p>
            <h1
              className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 max-w-3xl"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Planejamento <span className="text-emerald-700">Tributário</span>
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mb-8">
              Consultoria e planejamento tributário para empresas, recuperação de créditos fiscais, 
              análise de riscos e defesa em execuções fiscais e autos de infração.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white px-8 py-4 rounded font-medium transition-all">
                <MessageCircle className="w-5 h-5" />
                Fale com Especialista
              </a>
              <Link to="/contato"
                className="inline-flex items-center justify-center border-2 border-gray-300 hover:border-emerald-700 text-gray-700 hover:text-emerald-700 px-8 py-4 rounded font-medium transition-all">
                Enviar Mensagem
              </Link>
            </div>
          </div>
        </section>

        {/* Quando procurar */}
        <section className="py-16 bg-white border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                Quando Procurar Consultoria Tributária?
              </h2>
              <p className="text-gray-600 leading-relaxed">
                Se sua empresa está pagando mais impostos do que deveria, recebeu um auto de infração, 
                está sendo executada fiscalmente ou deseja estruturar suas operações de forma mais 
                eficiente do ponto de vista tributário.
              </p>
            </div>
          </div>
        </section>

        {/* Serviços */}
        <section className="py-20 bg-gray-50 relative overflow-hidden">
          <div className="absolute left-0 bottom-0 opacity-[0.02] pointer-events-none">
            <img src={LOGO_ICON} alt="" className="w-[400px] h-auto" style={{ filter: "brightness(0)" }} />
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-10" style={{ fontFamily: "'Playfair Display', serif" }}>
              Nossos Serviços Tributários
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {servicos.map((servico, index) => (
                <div key={index} className="bg-white p-6 rounded-lg border border-gray-100 hover:shadow-md transition-all">
                  <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center text-emerald-700 mb-4">
                    {servico.icon}
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-2">{servico.title}</h4>
                  <p className="text-gray-600 text-sm">{servico.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Diferencial */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Por Que Investir em Consultoria Tributária?
                </h2>
                <div className="space-y-4">
                  {[
                    "Redução legal da carga tributária",
                    "Recuperação de créditos pagos indevidamente",
                    "Prevenção de autuações e multas",
                    "Defesa em processos administrativos e judiciais",
                    "Compliance fiscal e segurança jurídica",
                    "Otimização do fluxo de caixa",
                  ].map((item, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-emerald-50 p-8 rounded-xl border border-emerald-100">
                <Building2 className="w-10 h-10 text-emerald-700 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Para Empresas de Todos os Portes</h3>
                <p className="text-gray-600 mb-6">
                  Micro, pequenas, médias e grandes empresas podem se beneficiar de uma gestão 
                  tributária eficiente. A economia gerada muitas vezes supera o investimento.
                </p>
                <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded font-medium transition-all w-full">
                  <MessageCircle className="w-5 h-5" />
                  Solicitar Diagnóstico
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-20 bg-gray-50 relative overflow-hidden">
          <div className="absolute right-0 bottom-0 opacity-[0.02] pointer-events-none">
            <img src={LOGO_ICON} alt="" className="w-[400px] h-auto" style={{ filter: "brightness(0)" }} />
          </div>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center mb-12">
              <HelpCircle className="w-10 h-10 text-emerald-700 mx-auto mb-4" />
              <h2 
                className="text-2xl md:text-3xl font-bold text-gray-900 mb-4"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Perguntas Frequentes
              </h2>
              <p className="text-gray-600">
                Tire suas principais dúvidas sobre Direito Tributário
              </p>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div 
                  key={index}
                  className="border border-gray-200 rounded-lg overflow-hidden bg-white"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 transition-colors"
                  >
                    <span className="font-medium text-gray-900 pr-4">{faq.pergunta}</span>
                    <ChevronDown 
                      className={`w-5 h-5 text-gray-500 flex-shrink-0 transition-transform ${
                        openFaq === index ? "rotate-180" : ""
                      }`} 
                    />
                  </button>
                  {openFaq === index && (
                    <div className="px-5 pb-5 pt-0">
                      <p className="text-gray-600 leading-relaxed">{faq.resposta}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-emerald-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              Sua Empresa Está Pagando Impostos a Mais?
            </h2>
            <p className="text-emerald-100 mb-8 max-w-xl mx-auto">
              Solicite uma análise tributária e descubra oportunidades de economia.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded font-medium transition-all">
                <MessageCircle className="w-5 h-5" />
                Falar pelo WhatsApp
              </a>
              <Link to="/contato"
                className="inline-flex items-center justify-center border-2 border-white/30 hover:border-white text-white px-8 py-4 rounded font-medium transition-all">
                Enviar Mensagem
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default TributarioPage;
